import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Paper, Stack, Tabs, Text, Title } from "@mantine/core";

import type { Insurance } from "../../hooks/api/useGetCalculationById";
import type {
  InsuranceListProps,
  PolicyRecommendationsProps,
  PolicyRecommendationsTab,
} from "../../types";
import InsurancesListItem from "../insurancesListItem";
import { FundSwitchCard } from "./FundSwitchCard";
import { sortByImpact } from "./policies";
import classes from "./index.module.css";

const TAB_LABELS: Record<PolicyRecommendationsTab, JSX.Element> = {
  fundSwitches: (
    <FormattedMessage
      id="tab.insuranceList.fundSwitch"
      defaultMessage="Fondbyten"
    />
  ),
  transfers: (
    <FormattedMessage
      id="tab.insuranceList.transferable"
      defaultMessage="Flytt"
    />
  ),
  others: (
    <FormattedMessage id="tab.insuranceList.other" defaultMessage="Övriga" />
  ),
};

function InsuranceList({
  insurances,
  isTransfer,
  showMenu,
}: InsuranceListProps) {
  return (
    <Box component="ul" role="list" className={classes.list}>
      {insurances.map((insurance, index) => (
        <Box component="li" key={`${insurance.insuranceIdentifier}-${index}`}>
          <InsurancesListItem
            insurance={insurance as Insurance}
            isTransfer={isTransfer}
            showMenu={showMenu}
            showCapital
            avatarSize="md"
          />
        </Box>
      ))}
    </Box>
  );
}

export function PolicyRecommendations({
  fundSwitches,
  transfers,
  managed = [],
  others = [],
  showOthersTab = true,
  adviceAge,
  title = (
    <FormattedMessage
      id="pensionOverview.list.headline"
      defaultMessage="Dina pensioner"
    />
  ),
  description = (
    <FormattedMessage
      id="pensionOverview.list.paragraph"
      defaultMessage="Här ser du dina försäkringar uppdelade efter rekommendation."
    />
  ),
  transfersHeading = (
    <FormattedMessage
      id="insuranceList.inTransfer.title"
      defaultMessage="Under flytt till IdunLiv"
    />
  ),
  transfersFooter = null,
  fundSwitchesFooter = null,
}: PolicyRecommendationsProps) {
  const [chosenTab, setChosenTab] = useState<PolicyRecommendationsTab | null>(
    null,
  );

  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const inTransfer = new Set(
    [...transfers, ...managed].map((policy) => policy.insuranceIdentifier),
  );
  const orderedFundSwitches = sortByImpact(
    fundSwitches.filter(
      (policy) => !inTransfer.has(policy.insuranceIdentifier),
    ),
  );

  const hasFundSwitches = orderedFundSwitches.length > 0;
  const hasTransfers = managed.length > 0 || transfers.length > 0;
  const hasOthers = showOthersTab && others.length > 0;

  const categoriesWithContent = (
    [
      hasFundSwitches && "fundSwitches",
      hasTransfers && "transfers",
      hasOthers && "others",
    ] as const
  ).filter((category): category is PolicyRecommendationsTab => !!category);
  const onlyCategory =
    categoriesWithContent.length === 1 ? categoriesWithContent[0] : null;

  const tab =
    chosenTab && categoriesWithContent.includes(chosenTab)
      ? chosenTab
      : categoriesWithContent[0];

  const fundSwitchesSection = (
    <Stack gap="md">
      <Text size="md" fw={700}>
        <FormattedMessage
          id="insuranceList.fundSwitch.title"
          defaultMessage="Rekommenderade fondbyten"
        />
      </Text>
      {hasFundSwitches && (
        <Stack component="ul" gap="md" className={classes.list}>
          {orderedFundSwitches.map((policy, index) => {
            const cardKey = `${policy.insuranceIdentifier}-${index}`;
            return (
              <FundSwitchCard
                key={cardKey}
                policy={policy}
                adviceAge={adviceAge}
                opened={expandedCard === cardKey}
                onToggle={() =>
                  setExpandedCard((current) =>
                    current === cardKey ? null : cardKey,
                  )
                }
              />
            );
          })}
        </Stack>
      )}
      {fundSwitchesFooter}
    </Stack>
  );

  const transfersSection = (
    <Stack gap="md">
      {transfers.length > 0 && (
        <>
          <Text size="sm" fw={700}>
            {transfersHeading}
          </Text>
          <InsuranceList insurances={transfers} isTransfer />
        </>
      )}
      {managed.length > 0 && (
        <>
          <Text size="sm" fw={700}>
            <FormattedMessage
              id="insuranceList.transferred.title"
              defaultMessage="Redan flyttade via Sambla"
            />
          </Text>
          <InsuranceList insurances={managed} showMenu />
        </>
      )}
      {transfersFooter}
    </Stack>
  );

  const othersSection = (
    <Stack gap="md">
      {others.length > 0 && (
        <>
          <Text size="sm" fw={700}>
            <FormattedMessage
              id="insuranceList.other.title"
              defaultMessage="Försäkringar som är oförändrade"
            />
          </Text>
          <InsuranceList insurances={others} />
        </>
      )}
    </Stack>
  );

  const sections: Record<PolicyRecommendationsTab, JSX.Element> = {
    fundSwitches: fundSwitchesSection,
    transfers: transfersSection,
    others: othersSection,
  };

  return (
    <Paper p={{ base: "md", sm: "xl" }} radius="lg" mb="xl">
      <Stack gap="md">
        <Stack gap={4}>
          <Title ff="var(--wallet-font-family-sambla-sans)" order={2} size="h3">
            {title}
          </Title>
          {description && (
            <Text size="md" c="var(--wallet-color-sambla-group-neutrals-500)">
              {description}
            </Text>
          )}
        </Stack>

        {onlyCategory && sections[onlyCategory]}

        {categoriesWithContent.length > 1 && (
          <Tabs
            value={tab}
            onChange={(value) =>
              value && setChosenTab(value as PolicyRecommendationsTab)
            }
            variant="filledCta"
            keepMounted={false}
            classNames={{ list: classes.tabsList, tab: classes.tab }}
            styles={{ tab: { color: "var(--wallet-color-neutrals-black)" } }}
          >
            <Tabs.List grow>
              {categoriesWithContent.map((category) => (
                <Tabs.Tab key={category} value={category}>
                  {TAB_LABELS[category]}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {categoriesWithContent.map((category) => (
              <Tabs.Panel key={category} value={category} pt="md">
                {sections[category]}
              </Tabs.Panel>
            ))}
          </Tabs>
        )}
      </Stack>
    </Paper>
  );
}
